import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SetupInterlude } from "@/components/setup/setup-interlude";
import { StepEditor } from "@/components/setup/step-editor";
import { Stepper } from "@/components/setup/stepper";
import { STEP_ORDER, STEPS, type SetupStepId } from "@/components/setup/steps";
import type { GetMySetupResp } from "@/lib/types";

/**
 * The guided first-run setup wizard — linear steps only. Later edits
 * happen on the order dashboard (/my-orders/:orderId), which reuses the
 * same step components. Every step saves on its primary button — closing
 * the tab never loses more than the step being edited.
 */
export function SetupWizard({
  orderId,
  setup,
}: {
  orderId: string;
  setup: GetMySetupResp;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStepId>("basics");
  const stepIndex = STEP_ORDER.indexOf(step);

  // The "your page is live" interlude shows once, right after the
  // basics save creates the site — not again when the buyer goes Back
  // and re-saves.
  const [interlude, setInterlude] = useState(false);
  const interludeShown = useRef(false);

  const advance = () => {
    if (step === "done") {
      navigate(`/my-orders/${orderId}`);
      return;
    }
    if (step === "basics" && !interludeShown.current) {
      interludeShown.current = true;
      setInterlude(true);
    }
    setStep(STEP_ORDER[stepIndex + 1] ?? "done");
  };
  const back = () => {
    const prev = STEP_ORDER[stepIndex - 1];
    if (prev) setStep(prev);
  };

  return (
    <div className="space-y-6">
      <Stepper
        steps={STEPS}
        current={stepIndex}
        onJump={
          interlude ? undefined : (i) => setStep(STEP_ORDER[i] ?? "basics")
        }
      />
      {interlude ? (
        <SetupInterlude
          onContinue={() => setInterlude(false)}
          onFinishLater={() => navigate(`/my-orders/${orderId}`)}
        />
      ) : (
        <StepEditor
          step={step}
          orderId={orderId}
          setup={setup}
          linear
          onDone={advance}
          onBack={step === "basics" ? null : back}
        />
      )}
    </div>
  );
}
